import React, { useState, useRef, useEffect } from 'react';
import { exportToPDF, exportToXLSX } from '../../utils/exportData';
import './ExportButton.css';

const ExportButton = ({
  data = [],
  columns = [],
  format = 'both',
  filename = 'export',
  title = '',
  subtitle = '',
  buttonText = 'Export',
  buttonVariant = 'primary',
  buttonSize = 'sm',
  pdfOptions = {},
  xlsxOptions = {},
  className = '',
}) => {
  const [loading, setLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showOptions) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showOptions]);

  if (!data || data.length === 0 || !columns || columns.length === 0) {
    return null;
  }

  const handleExport = async (exportFormat) => {
    setLoading(true);
    try {
      if (exportFormat === 'pdf') {
        exportToPDF(data, columns, { title, subtitle, filename, ...pdfOptions });
      } else {
        exportToXLSX(data, columns, { title, filename, ...xlsxOptions });
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setLoading(false);
      setShowOptions(false);
    }
  };

  const singleButton = (
    <button
      className={`export-btn export-btn--${buttonVariant} export-btn--${buttonSize} ${loading ? 'export-btn--loading' : ''} ${className}`}
      onClick={() => handleExport(format)}
      disabled={loading}
    >
      {loading ? (
        <>
          <svg className="export-btn__spinner" viewBox="0 0 24 24">
            <path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8Z" fill="currentColor" />
          </svg>
          Exporting...
        </>
      ) : (
        <>
          <svg className="export-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {buttonText}
        </>
      )}
    </button>
  );

  if (format !== 'both') {
    return singleButton;
  }

  return (
    <div className="export-btn__wrapper" ref={wrapperRef}>
      <button
        className={`export-btn export-btn--${buttonVariant} export-btn--${buttonSize} ${loading ? 'export-btn--loading' : ''} ${className}`}
        onClick={() => setShowOptions(!showOptions)}
        disabled={loading}
      >
        {loading ? (
          <>
            <svg className="export-btn__spinner" viewBox="0 0 24 24">
              <path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8Z" fill="currentColor" />
            </svg>
            Exporting...
          </>
        ) : (
          <>
            <svg className="export-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {buttonText}
          </>
        )}
      </button>
      {showOptions && (
        <div className="export-btn__dropdown">
          <button
            className="export-btn__option"
            onClick={() => handleExport('pdf')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Export PDF
          </button>
          <button
            className="export-btn__option"
            onClick={() => handleExport('xlsx')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
            Export Excel
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportButton;
